import React from "react";
import SearchableSelectField from "../NewCase/ui/SearchableSelectField";

/*
Form primitives shared by the four detail tabs.

Field/TextAreaField/CheckboxGrid/ToggleRow are inert by default: much of this
screen has no endpoint behind it, and an editable field that silently discards
what you type is worse than a visibly inert one. Passing `onChange` turns one
into a controlled, editable control - do that only for fields the page's Save
(PATCH /ticket/{id}/, see useTicketDraft.js) actually sends. EditableField is
for values with their own endpoint and an inline Save button (see ProductTab.js).
*/

export const FieldError = function (props) {
  return props.error ? <p className="tn-field-error">{props.error}</p> : null;
};

// Backend confirmed (2026-09-25) that `assigned_to`/`technician` fields come
// back as a bare Employee id, not a name - there's no lookup for the name
// wired up here, so show the id rather than silently rendering nothing.
export const employeeLabel = function (id) {
  if (id == null) return undefined;
  return "Employee #" + id;
};

export const FormSection = function (props) {
  return (
    <section className="tn-card tn-formsection">
      <div className="tn-card-body">
        <div className="tn-formsection-head">
          <h3 className="tn-formsection-title">{props.title}</h3>
          {props.note ? (
            <span className="tn-formsection-note">{props.note}</span>
          ) : null}
        </div>
        {props.children}
      </div>
    </section>
  );
};

/*
Without `onChange`, `value`/`selected`/`checked`/`files` are read-outs from a
real ticket, passed as defaultValue/defaultChecked (uncontrolled) - the field
stays disabled, so there's nothing to pair a controlled prop with. With
`onChange` (called with the new value, not the event) they're controlled, and
`disabled`/`error` apply.
*/
export const Field = function (props) {
  const editable = typeof props.onChange === "function";
  return (
    <div className="tn-field">
      <label className="tn-field-label" htmlFor={props.id}>
        {props.label}
      </label>
      {editable ? (
        <input
          id={props.id}
          type={props.type || "text"}
          className={"tn-input" + (props.error ? " tn-input-invalid" : "")}
          placeholder={props.placeholder}
          value={props.value == null ? "" : props.value}
          onChange={function (e) {
            props.onChange(e.target.value);
          }}
          disabled={props.disabled}
        />
      ) : (
        <input
          type={props.type || "text"}
          className="tn-input"
          placeholder={props.placeholder}
          defaultValue={props.value == null ? undefined : props.value}
          disabled
        />
      )}
      <FieldError error={props.error} />
    </div>
  );
};

/*
Every dropdown on this screen: NewCase's SearchableSelectField (so both screens
share one combo box), with the case-detail defaults on top.
- `tn-select` sizes it like a tn-input (TicketCaseDetail.css).
- menuPosition "fixed" lets the menu escape .tn-card's overflow: hidden, so it
  isn't clipped at the card edge. A fixed menu doesn't follow the page when it
  scrolls, so it closes on any scroll outside its own list instead.
- Not clearable by default: none of these fields can be saved empty.
`value` must match an option's value exactly (===), like in NewCase.
*/
const closeMenuOnOuterScroll = function (event) {
  const target = event.target;
  return !(target && target.closest && target.closest(".rma-nc-rs__menu"));
};

export const SelectControl = function (props) {
  return (
    <SearchableSelectField
      isClearable={false}
      placeholder="Select…"
      menuPosition="fixed"
      menuPlacement="auto"
      closeMenuOnScroll={closeMenuOnOuterScroll}
      {...props}
      className={"tn-select" + (props.className ? " " + props.className : "")}
    />
  );
};

/*
Controlled counterpart to Field with an inline Save button, for the few values
that have a real save endpoint. Passing `options` ([{ value, label }]) renders
a SelectControl instead of a text input. With `disabled` (e.g. the role can't
edit) it renders as an inert Field-alike with no Save button.
*/
export const EditableField = function (props) {
  const value = props.value == null ? "" : props.value;

  const control = props.options ? (
    <SelectControl
      inputId={props.id}
      placeholder={props.placeholder}
      options={props.options}
      value={value}
      onChange={props.onChange}
      isLoading={props.loading}
      isDisabled={props.disabled || props.saving}
    />
  ) : (
    <input
      id={props.id}
      type={props.type || "text"}
      className="tn-input"
      placeholder={props.placeholder}
      value={value}
      onChange={function (e) {
        props.onChange(e.target.value);
      }}
      disabled={props.disabled || props.saving}
    />
  );

  return (
    <div className="tn-field">
      <label className="tn-field-label" htmlFor={props.id}>
        {props.label}
      </label>
      <div className="tn-field-inline">
        {control}
        {props.disabled ? null : (
          <button
            type="button"
            className="tn-btn tn-btn-primary tn-btn-sm"
            disabled={!props.dirty || props.saving}
            onClick={props.onSave}
          >
            {props.saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      {props.hint ? <p className="tn-field-hint">{props.hint}</p> : null}
    </div>
  );
};

// Each entry in `fields` is a Field's props (so an entry with `onChange` is
// editable). `children` render after the generated fields, inside the same
// grid - for mixing an EditableField in with the others.
export const FieldGrid = function (props) {
  return (
    <div className={"tn-fieldgrid tn-fieldgrid-" + (props.cols || 3)}>
      {props.fields.map(function (field) {
        return <Field key={field.label} {...field} />;
      })}
      {props.children}
    </div>
  );
};

// `action` (e.g. an Edit button) renders on the label row, right-aligned.
// `hint` renders under the textarea. Editable with `onChange`, as Field.
export const TextAreaField = function (props) {
  const editable = typeof props.onChange === "function";
  const label = (
    <label className="tn-field-label" htmlFor={props.id}>
      {props.label}
    </label>
  );
  return (
    <div className="tn-field">
      {props.action ? (
        <div className="tn-field-label-row">
          {label}
          {props.action}
        </div>
      ) : (
        label
      )}
      {editable ? (
        <textarea
          id={props.id}
          className={"tn-textarea" + (props.error ? " tn-input-invalid" : "")}
          placeholder={props.placeholder}
          value={props.value == null ? "" : props.value}
          onChange={function (e) {
            props.onChange(e.target.value);
          }}
          disabled={props.disabled}
        />
      ) : (
        <textarea
          className="tn-textarea"
          placeholder={props.placeholder}
          defaultValue={props.value == null ? undefined : props.value}
          disabled
        />
      )}
      {props.hint ? <p className="tn-field-hint">{props.hint}</p> : null}
      <FieldError error={props.error} />
    </div>
  );
};

// `selected` is a list of raw values from the ticket (e.g. fault_types); the
// match against each option label is case-insensitive since the API sends
// lowercase keys ("leakage") against Title Case option labels ("Leakage").
// `onToggle(option, checked)` makes it editable (controlled by `selected`);
// `disabledOptions` then locks individual options (e.g. ones with no field).
export const CheckboxGrid = function (props) {
  const editable = typeof props.onToggle === "function";
  const selected = (props.selected || []).map(function (value) {
    return String(value).trim().toLowerCase();
  });
  const disabledOptions = props.disabledOptions || [];
  return (
    <div>
      <div className="tn-checkgrid">
        {props.options.map(function (option) {
          const isChecked = selected.indexOf(option.toLowerCase()) !== -1;
          const isDisabled = !!props.disabled || disabledOptions.indexOf(option) !== -1;
          return (
            <label
              className={"tn-check" + (editable && !isDisabled ? " tn-check-editable" : "")}
              key={option}
            >
              {editable ? (
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={function (e) {
                    props.onToggle(option, e.target.checked);
                  }}
                  disabled={isDisabled}
                />
              ) : (
                <input type="checkbox" defaultChecked={isChecked} disabled />
              )}
              <span>{option}</span>
            </label>
          );
        })}
      </div>
      <FieldError error={props.error} />
    </div>
  );
};

// `onChange(checked)` makes it editable (controlled by `checked`). The
// read-only read-out is locked without the `disabled` attribute so it keeps
// its normal (accent) colour instead of the browser's greyed-out look.
export const ToggleRow = function (props) {
  const editable = typeof props.onChange === "function";
  return (
    <div>
      <label className={"tn-toggle" + (editable && !props.disabled ? " tn-toggle-editable" : "")}>
        <span>{props.label}</span>
        {editable ? (
          <input
            type="checkbox"
            checked={!!props.checked}
            onChange={function (e) {
              props.onChange(e.target.checked);
            }}
            disabled={props.disabled}
          />
        ) : (
          <input
            type="checkbox"
            checked={!!props.checked}
            readOnly
            tabIndex={-1}
            aria-disabled="true"
            onClick={function (e) {
              e.preventDefault();
            }}
          />
        )}
      </label>
      <FieldError error={props.error} />
    </div>
  );
};

/*
Yes/No radio pair for a nullable boolean (`value` true/false/null - null
leaves both unpicked, as on tickets that never answered the question).
Controlled; `onChange(true|false)`.
*/
export const YesNoField = function (props) {
  const option = function (label, optionValue) {
    return (
      <label className={"tn-check" + (props.disabled ? "" : " tn-check-editable")}>
        <input
          type="radio"
          name={props.id}
          checked={props.value === optionValue}
          onChange={function () {
            props.onChange(optionValue);
          }}
          disabled={props.disabled}
        />
        <span>{label}</span>
      </label>
    );
  };
  return (
    <div className="tn-field">
      <span className="tn-field-label">{props.label}</span>
      <div className="tn-radiorow tn-radiorow-tight">
        {option("Yes", true)}
        {option("No", false)}
      </div>
      <FieldError error={props.error} />
    </div>
  );
};

/*
Single-choice list of stacked option cards, like NewCase's RadioGroup with
`hideIndicator` (the card highlight carries the selection). `options` is
[{ value, label }]; `value` must match an option's value exactly (===).
Controlled; `onChange(optionValue)`.
*/
export const RadioListField = function (props) {
  return (
    <div className="tn-field">
      <span className="tn-field-label">{props.label}</span>
      <div className="tn-radiolist">
        {props.options.map(function (option) {
          const isSelected = props.value === option.value;
          return (
            <label
              key={option.value}
              className={
                "tn-check" +
                (props.disabled ? "" : " tn-check-editable") +
                (isSelected ? " tn-check-selected" : "")
              }
            >
              <input
                type="radio"
                name={props.id}
                checked={isSelected}
                onChange={function () {
                  props.onChange(option.value);
                }}
                disabled={props.disabled}
              />
              <span>{option.label}</span>
            </label>
          );
        })}
      </div>
      <FieldError error={props.error} />
    </div>
  );
};

/*
Sticky bar under the active tab while the page's Save draft has changes (or
the last Save failed). `count` is the number of changed fields.
*/
export const SaveBar = function (props) {
  if (!props.count && !props.error) return null;
  return (
    <div className="tn-savebar" role="region" aria-label="Unsaved changes">
      <span className={"tn-savebar-text" + (props.error ? " tn-savebar-text-error" : "")}>
        {props.error ||
          props.count + (props.count === 1 ? " unsaved change" : " unsaved changes")}
      </span>
      <div className="tn-savebar-actions">
        <button
          type="button"
          className="tn-btn tn-btn-ghost tn-btn-sm"
          onClick={props.onDiscard}
          disabled={props.saving}
        >
          Discard
        </button>
        <button
          type="button"
          className="tn-btn tn-btn-primary tn-btn-sm"
          onClick={props.onSave}
          disabled={props.saving || !props.count}
        >
          {props.saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
};

// `files` is the ticket's attachments[] (or a subset) - shown as links below
// the dropzone rather than inside it.
//
// Passing `onFileChange` switches the dropzone from a plain display box (as
// ServiceTab uses it) into a clickable file picker: the box itself is the
// label for a hidden file input, `warning`/`selectedFileName` reflect the
// caller's validation state, and `onSave`/`onClear` render once a file is
// chosen so the caller can commit or drop the selection.
export const Dropzone = function (props) {
  const files = props.files || [];
  const interactive = typeof props.onFileChange === "function";
  const inputId = props.inputId || "tn-dropzone-file";

  return (
    <div>
      <div
        className={
          "tn-dropzone" + (props.warning ? " tn-dropzone-warning" : "")
        }
      >
        {interactive ? (
          <label htmlFor={inputId} className="tn-dropzone-label">
            <div className="tn-dropzone-title">
              {props.selectedFileName ||
                props.title ||
                "Drag & drop or click to browse"}
            </div>
            <div className="tn-dropzone-hint">{props.hint}</div>
          </label>
        ) : (
          <React.Fragment>
            <div className="tn-dropzone-title">
              {props.title || "Drag & drop or browse"}
            </div>
            <div className="tn-dropzone-hint">{props.hint}</div>
          </React.Fragment>
        )}
        {interactive ? (
          <input
            key={props.inputKey}
            type="file"
            id={inputId}
            data-testid={inputId}
            accept={props.accept}
            style={{ display: "none" }}
            onChange={props.onFileChange}
          />
        ) : null}
      </div>
      {props.warning ? (
        <div className="text-danger mt-2">
          <small>{props.warning}</small>
        </div>
      ) : null}
      {interactive && props.selectedFileName ? (
        <div className="tn-dropzone-actions">
          <button
            type="button"
            className="tn-btn tn-btn-primary tn-btn-sm"
            disabled={props.saveDisabled}
            onClick={props.onSave}
          >
            Save
          </button>
          <button
            type="button"
            className="tn-btn tn-btn-ghost tn-btn-sm"
            onClick={props.onClear}
          >
            Remove
          </button>
        </div>
      ) : null}
      {files.length ? (
        <ul className="tn-filelist">
          {files.map(function (file, index) {
            return (
              <li className="tn-filelist-item" key={file.id || index}>
                <a href={file.file_url} target="_blank" rel="noreferrer">
                  {file.file_name || file.file_url}
                </a>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default {
  employeeLabel,
  FormSection,
  Field,
  SelectControl,
  EditableField,
  FieldGrid,
  TextAreaField,
  CheckboxGrid,
  ToggleRow,
  YesNoField,
  RadioListField,
  SaveBar,
  Dropzone,
};
